<template>
  <div class="notice-detail-page">
    <div class="detail-card">
      <!-- 顶部工具条 -->
      <div class="card-toolbar">
        <div class="toolbar-left">
          <span class="back-link" @click="handleBack">
            <i class="el-icon-back"></i> 返回通知列表
          </span>
        </div>
        <div class="toolbar-title">通知详情 · 触达数据</div>
        <div class="toolbar-right">
          <el-button class="btn-withdraw" size="small" v-if="notice.noticeStatus === 2" @click="handleWithdraw" v-permission="'btn:notice:withdraw'">撤回</el-button>
          <el-button class="btn-export" size="small" @click="exportExcel" v-permission="'btn:notice:export'">导出</el-button>
        </div>
      </div>

      <div v-loading="loading" class="card-body">
        <!-- 信息卡 -->
        <div class="head-info">
          <div class="head-title-row">
            <span class="type-icon" :class="notice.noticeType === 1 ? 'type-activity' : 'type-system'">
              <i class="el-icon-mobile-phone"></i>
            </span>
            <span class="type-tag" :class="notice.noticeType === 1 ? 'tag-activity' : 'tag-system'">
              {{ notice.noticeType === 1 ? '活动' : '系统' }}
            </span>
            <span class="notice-name">{{ notice.noticeName }}</span>
            <span class="status-tag" :class="statusClass(notice.noticeStatus)">{{ statusText(notice.noticeStatus) }}</span>
          </div>
          <div class="head-meta-row">
            <span>发布时间：{{ notice.publishTime | formatShort }}</span>
            <span v-if="notice.noticeType === 1">报名截止：{{ notice.deadlineTime | formatDate }}</span>
            <span>范围：{{ notice.targetShopType === 2 ? '指定 ' : '全部 ' }}{{ notice.userCount || 0 }} 位店主</span>
          </div>
        </div>

        <!-- 触达漏斗 -->
        <div class="funnel-section">
          <div class="section-label">触达漏斗</div>
          <div class="funnel-list">
            <div v-for="(item, index) in funnelList" :key="index" class="funnel-item">
              <span class="funnel-arrow" v-if="index > 0"><i class="el-icon-arrow-right"></i></span>
              <div class="funnel-card" :class="item.cls">
                <div class="funnel-num">{{ item.num }}</div>
                <div class="funnel-label">{{ item.label }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tab + 搜索 -->
        <div class="table-section">
          <div class="tabs-row">
            <div class="tabs">
              <div
                v-for="tab in tabList"
                :key="tab.value + ''"
                class="tab-item"
                :class="{ active: activeTab === tab.value }"
                @click="switchTab(tab.value)"
              >
                {{ tab.label }} <span class="tab-count">{{ tab.count }}</span>
              </div>
            </div>
            <div class="search-box">
              <el-input
                v-model="detailSearch.keyword"
                placeholder="搜索 门店名称、店主姓名/手机号（精准）..."
                clearable
                size="small"
                @keyup.enter.native="doSearch"
                @clear="doSearch"
              >
                <i slot="suffix" class="el-input__icon el-icon-search" @click.stop="doSearch"></i>
              </el-input>
              <el-button type="primary" size="small" @click="doSearch">搜索</el-button>
              <el-button size="small" @click="handleResetDetail">重置</el-button>
            </div>
          </div>

          <!-- 一个店主一行，门店列按门店纵向合并，其余列按店主逐行展示 -->
          <el-table :data="tableRows" border v-loading="detailLoading" size="small" style="width: 100%" :span-method="spanMethod">
            <el-table-column label="门店" width="130">
              <template slot-scope="scope">
                <div class="cell-shop">
                  <div class="shop-name">{{ scope.row.shopName }}</div>
                  <div class="shop-region">{{ shopRegionText(scope.row) }}</div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="店主" width="180">
              <template slot-scope="scope">
                <div class="cell-owner">
                  <div class="owner-name">{{ ownerNameText(scope.row) }}</div>
                  <div class="owner-phone-line">
                    <span class="owner-phone">{{ ownerPhoneText(scope.row) }}</span>
                    <i
                      class="eye-toggle"
                      :class="isOwnerRevealed(scope.row) ? 'el-icon-hide' : 'el-icon-view'"
                      :title="isOwnerRevealed(scope.row) ? '隐藏' : '查看'"
                      @click.stop="toggleOwnerReveal(scope.row)"
                    ></i>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="已读时间" width="150">
              <template slot-scope="scope">
                {{ scope.row.readTime | formatDate }}
              </template>
            </el-table-column>
            <el-table-column label="确认状态" width="140">
              <template slot-scope="scope">
                <span v-if="statusBadge(scope.row)" class="status-badge" :class="statusBadge(scope.row).cls">
                  <i class="status-icon" :class="'status-icon-' + statusBadge(scope.row).icon">{{ statusBadge(scope.row).symbol }}</i>{{ statusBadge(scope.row).label }}
                </span>
                <span v-else>—</span>
              </template>
            </el-table-column>
            <!-- 否定理由/店主反馈为活动通知专属，系统通知不展示 -->
            <el-table-column v-if="notice.noticeType === 1" label="否定理由" min-width="230" show-overflow-tooltip>
              <template slot-scope="scope">
                <span v-if="negReasonText(scope.row)" class="neg-reason">
                  <i class="neg-reason-icon">!</i>{{ negReasonText(scope.row) }}
                </span>
                <span v-else>—</span>
              </template>
            </el-table-column>
            <el-table-column v-if="notice.noticeType === 1" label="店主反馈" min-width="200" show-overflow-tooltip>
              <template slot-scope="scope">
                <span v-if="scope.row.suggestContent">{{ scope.row.suggestContent }}</span>
                <span v-else>—</span>
              </template>
            </el-table-column>
          </el-table>

          <div class="table-footer">
            <div class="footer-total">共 {{ pagination.total }} 家门店</div>
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="pagination.page"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pagination.limit"
              layout="total, sizes, prev, pager, next"
              :total="pagination.total"
            ></el-pagination>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { aesDecrypt, aesEncrypt } from '../../utils/aes'

export default {
  name: 'NoticeDetail',
  filters: {
    formatDate(val) {
      if (!val) return '-'
      const d = new Date(val)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    },
    formatShort(val) {
      if (!val) return '—'
      const d = new Date(val)
      return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    }
  },
  data() {
    return {
      noticeId: null,
      loading: false,
      notice: {},
      stat: {},
      detailList: [],
      detailLoading: false,
      activeTab: null,
      // 当前展开明文的店主明细ID（同屏只允许展开一条，点击其他行自动收起上一条）
      revealedOwnerId: null,
      detailSearch: {
        shopReadStatus: null,
        keyword: ''
      },
      pagination: {
        page: 1,
        limit: 10,
        total: 0
      }
    }
  },
  computed: {
    // 后端按门店聚合返回，这里摊平为“一店主一行”，并标记是否为该门店的首行
    tableRows() {
      const rows = []
      this.detailList.forEach(group => {
        const owners = group.owners || []
        // 没有店主明细的门店直接不渲染：否则会渲染出只有门店名、已读时间/确认状态全空的行
        if (!owners.length) return
        owners.forEach((owner, index) => {
          rows.push({
            ...owner,
            // 店主姓名/手机号入库为 AES 密文，展示前解密
            userName: aesDecrypt(owner.userName),
            userPhone: aesDecrypt(owner.userPhone),
            shopName: group.shopName,
            city: group.city,
            county: group.county,
            // 门店列合并跨度：首行跨该门店的全部店主，其余行为 0（由 span-method 隐藏）
            shopRowSpan: index === 0 ? owners.length : 0
          })
        })
      })
      return rows
    },
    funnelList() {
      const list = [
        { num: this.stat.total_count || 0, label: '总数', cls: 'funnel-gray' },
        { num: this.stat.read_count || 0, label: '已读', cls: 'funnel-gray' }
      ]
      // 已确认参加/不参加是活动通知专属，系统通知不展示
      if (this.notice.noticeType === 1) {
        list.push(
          { num: this.stat.join_count || 0, label: '已确认参加', cls: 'funnel-green' },
          { num: this.stat.not_join_count || 0, label: '不参加', cls: 'funnel-red' }
        )
      }
      list.push({ num: this.stat.unread_count || 0, label: '未读', cls: 'funnel-yellow' })
      return list
    },
    tabList() {
      const list = [
        { label: '全部', value: null, count: this.stat.total_count || 0 }
      ]
      // 已确认/不参加是活动通知专属，系统通知不展示
      if (this.notice.noticeType === 1) {
        list.push(
          { label: '已确认', value: 3, count: this.stat.join_count || 0 },
          { label: '不参加', value: 4, count: this.stat.not_join_count || 0 }
        )
      }
      list.push(
        { label: '已读', value: 2, count: this.stat.read_count || 0 },
        { label: '未读', value: 1, count: this.stat.unread_count || 0 }
      )
      // 已逾期是活动通知专属（报名截止相关），系统通知不展示
      if (this.notice.noticeType === 1) {
        list.push({ label: '已逾期', value: 5, count: this.stat.overdue_count || 0 })
      }
      return list
    }
  },
  mounted() {
    this.noticeId = Number(this.$route.params.id)
    // 无效 id（如标签残留的过期路径）直接回列表，避免请求 /detail/NaN 报“加载通知详情失败”
    if (!this.noticeId) {
      this.$router.replace('/noticeList')
      return
    }
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const [detailRes, statRes] = await Promise.all([
          this.$http.get('/shopNotice/detail/' + this.noticeId),
          this.$http.get('/shopNotice/stat/' + this.noticeId)
        ])
        if (detailRes.data.status === 0) {
          this.notice = detailRes.data.data.notice || {}
        }
        if (statRes.data.status === 0) {
          this.stat = statRes.data.data.stat || {}
        }
        await this.getDetailList()
      } catch (error) {
        console.error('加载通知详情失败:', error)
        this.$message.error('加载通知详情失败')
      } finally {
        this.loading = false
      }
    },
    async getDetailList() {
      this.detailLoading = true
      // 换页/切Tab/搜索后放弃上一屏的展开态，避免残留明文
      this.revealedOwnerId = null
      try {
        const params = {
          noticeId: this.noticeId,
          page: this.pagination.page,
          limit: this.pagination.limit,
          shopReadStatus: this.detailSearch.shopReadStatus != null ? this.detailSearch.shopReadStatus : undefined,
          // 门店名称走明文模糊匹配，店主姓名/手机号需加密后由后端做密文精准匹配
          keyword: this.detailSearch.keyword || undefined,
          encryptedKeyword: this.detailSearch.keyword ? aesEncrypt(this.detailSearch.keyword) : undefined
        }
        Object.keys(params).forEach(key => {
          if (params[key] === undefined || params[key] === '' || params[key] === null) {
            delete params[key]
          }
        })
        const res = await this.$http.get('/shopNotice/detailList', { params })
        if (res.data.status === 0) {
          this.detailList = res.data.data.list || []
          this.pagination.total = res.data.data.total || 0
        } else {
          this.$message.error(res.data.msg || '获取门店详情失败')
        }
      } catch (error) {
        console.error('获取门店详情失败:', error)
        this.$message.error('获取门店详情失败')
      } finally {
        this.detailLoading = false
      }
    },
    // 门店列纵向合并：首行返回该门店的店主数，其余行返回 0 由表格隐藏该单元格
    spanMethod({ row, columnIndex }) {
      if (columnIndex !== 0) return
      const rowspan = Number(row.shopRowSpan) || 0
      if (rowspan > 0) {
        return { rowspan, colspan: 1 }
      }
      return { rowspan: 0, colspan: 0 }
    },
    // 门店所在地：市 · 区
    shopRegionText(row) {
      const arr = [row.city, row.county].filter(v => v && String(v).trim())
      return arr.length ? arr.join(' · ') : '—'
    },
    switchTab(value) {
      if (this.activeTab === value) return
      this.activeTab = value
      this.detailSearch.shopReadStatus = value
      this.pagination.page = 1
      this.getDetailList()
    },
    doSearch() {
      this.pagination.page = 1
      this.getDetailList()
    },
    // 重置：清空关键词回到全部数据（不改动状态 tab）
    handleResetDetail() {
      this.detailSearch.keyword = ''
      this.pagination.page = 1
      this.getDetailList()
    },
    handleSizeChange(val) {
      this.pagination.limit = val
      this.pagination.page = 1
      this.getDetailList()
    },
    handleCurrentChange(val) {
      this.pagination.page = val
      this.getDetailList()
    },
    statusText(status) {
      const map = { 1: '草稿', 2: '已发布', 3: '已撤回', 4: '已结束' }
      return map[status] || '-'
    },
    statusClass(status) {
      const map = { 1: 'st-draft', 2: 'st-published', 3: 'st-withdraw', 4: 'st-ended' }
      return map[status] || ''
    },
    statusBadge(row) {
      const map = {
        1: { cls: 'badge-unread', icon: 'unread', symbol: '!', label: '未读' },
        2: { cls: 'badge-read', icon: 'read', symbol: '?', label: '已读待确认' },
        3: { cls: 'badge-join', icon: 'join', symbol: '✓', label: '已确认参加' },
        4: { cls: 'badge-notjoin', icon: 'notjoin', symbol: '✕', label: '不参加' },
        5: { cls: 'badge-overdue', icon: 'overdue', symbol: '!', label: '已逾期' }
      }
      const badge = map[row.shopReadStatus] || null
      if (badge && this.notice.noticeType !== 1) {
        // 系统通知没有“确认参加”交互：已读即终态，样式与活动通知的“已确认参加”保持一致（绿色✓）
        if (row.shopReadStatus === 2) {
          return { ...map[3], label: '已读' }
        }
        // 系统通知没有“已逾期”概念：历史脏数据的 5 按未读展示，避免出现系统通知专属外的状态
        if (row.shopReadStatus === 5) {
          return { ...map[1], label: '未读' }
        }
      }
      return badge
    },
    negReasonText(row) {
      const raw = row && row.negReasonJson
      if (!raw) return ''
      let parsed = raw
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed)
        } catch (e) {
          // 非 JSON 串：历史数据可能直接存了纯文本理由
          return String(raw).trim()
        }
      }
      // 取单项文本：字符串，或 { text } 对象
      const itemText = item => {
        if (!item) return ''
        return typeof item === 'string' ? item : String(item.text || '')
      }
      // 旧格式：字符串数组 / [{ text }]
      if (Array.isArray(parsed)) {
        return parsed.map(itemText).filter(Boolean).join('、')
      }
      if (parsed && typeof parsed === 'object') {
        // 新格式：{ options: string[], text: string }（与小程序端提交结构一致）
        const options = Array.isArray(parsed.options) ? parsed.options.map(itemText).filter(Boolean) : []
        const text = parsed.text ? String(parsed.text).trim() : ''
        if (!options.length && !text) return ''
        if (options.length && text) return `${options.join('、')}（${text}）`
        return options.join('&') || text
      }
      return ''
    },
    // 行唯一标识：明细主键优先，缺失时用 门店+店主 兜底
    ownerRowKey(row) {
      return row.id != null ? row.id : (row.shopId + '-' + row.userId)
    },
    // 点击小眼睛：展开当前行明文，同屏只保留一条，点开下一条时上一条自动恢复脱敏
    toggleOwnerReveal(row) {
      const key = this.ownerRowKey(row)
      this.revealedOwnerId = this.revealedOwnerId === key ? null : key
    },
    isOwnerRevealed(row) {
      return this.revealedOwnerId !== null && this.revealedOwnerId === this.ownerRowKey(row)
    },
    // 店主姓名：展开态显示明文，否则脱敏（未实名时姓名兜底为手机号，按手机号规则脱敏）
    ownerNameText(row) {
      const name = row.userName || ''
      return this.isOwnerRevealed(row) ? (name || '—') : this.maskName(name)
    },
    // 店主手机号：展开态显示明文，否则脱敏
    ownerPhoneText(row) {
      const phone = row.userPhone || ''
      return this.isOwnerRevealed(row) ? (phone || '—') : this.maskPhone(phone)
    },
    maskName(name) {
      if (!name) return '—'
      const s = String(name)
      if (/^\d{11}$/.test(s)) return this.maskPhone(s)
      if (s.length <= 1) return s
      return s.charAt(0) + new Array(s.length).join('*')
    },
    maskPhone(phone) {
      if (!phone) return '—'
      const p = String(phone)
      if (p.length < 7) return p
      return p.substring(0, 3) + '****' + p.substring(p.length - 4)
    },
    async handleWithdraw() {
      try {
        const { value: remark } = await this.$prompt('请输入撤回备注（选填）', '撤回通知', {
          confirmButtonText: '确认撤回',
          cancelButtonText: '取消',
          inputPlaceholder: '请输入撤回备注'
        })
        const res = await this.$http.post('/shopNotice/withdraw/' + this.noticeId + '?remark=' + encodeURIComponent(remark || ''))
        if (res.data.status === 0) {
          this.$message.success('撤回成功')
          this.loadData()
        } else {
          this.$message.error(res.data.msg || '撤回失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('撤回通知失败:', error)
          this.$message.error('撤回通知失败')
        }
      }
    },
    async exportExcel() {
      try {
        const res = await this.$http.get('/shopNotice/export/' + this.noticeId, {
          responseType: 'blob'
        })
        const blob = res.data
        // 后端校验失败时返回的是 JSON 错误体，需先识别再提示
        if (blob && blob.type && blob.type.indexOf('application/json') !== -1) {
          const text = await this.readBlobText(blob)
          let msg = '导出失败'
          try {
            msg = JSON.parse(text).msg || msg
          } catch (e) {
            // 解析失败时使用默认提示
          }
          this.$message.error(msg)
          return
        }
        const fileName = this.parseFileName(res.headers) || ('通知店主统计-' + (this.notice.noticeName || '') + '.xlsx')
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败')
      }
    },
    readBlobText(blob) {
      return new Promise(resolve => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result || '')
        reader.onerror = () => resolve('')
        reader.readAsText(blob, 'utf-8')
      })
    },
    parseFileName(headers) {
      const disposition = headers && (headers['content-disposition'] || headers['Content-Disposition'])
      if (!disposition) return ''
      const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition)
      if (!match || !match[1]) return ''
      try {
        return decodeURIComponent(match[1])
      } catch (e) {
        return match[1]
      }
    },
    handleBack() {
      this.$router.push('/noticeList')
    }
  }
}
</script>

<style scoped>
.notice-detail-page {
  padding: 20px;
}
.detail-card {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
/* 顶部工具条 */
.card-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #f7f8fa;
  border-bottom: 1px solid #ebeef5;
}
.toolbar-left {
  min-width: 140px;
}
.back-link {
  color: #409eff;
  cursor: pointer;
  font-size: 14px;
}
.back-link:hover {
  opacity: 0.8;
}
.toolbar-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.toolbar-right {
  min-width: 140px;
  text-align: right;
}
.btn-withdraw {
  border-color: #dcdfe6;
  color: #606266;
  background: #fff;
}
.btn-withdraw:hover {
  border-color: #f56c6c;
  color: #f56c6c;
  background: #fff;
}
.btn-export {
  background: #303133;
  border-color: #303133;
  color: #fff;
}
.btn-export:hover {
  opacity: 0.85;
}
.card-body {
  padding: 20px;
}
/* 信息卡 */
.head-info {
  background: #f5f6f8;
  border-radius: 6px;
  padding: 14px 18px;
}
.head-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.type-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: #fff;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 6px;
}
.type-activity {
  background: #ff8533;
}
.type-system {
  background: #909399;
}
.type-tag {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 3px;
  margin-right: 10px;
}
.tag-activity {
  color: #ff7a1a;
  background: #fff1e6;
}
.tag-system {
  color: #606266;
  background: #f0f0f0;
}
.notice-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-right: 10px;
}
.status-tag {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid;
}
.st-draft {
  color: #909399;
  border-color: #dcdfe6;
}
.st-published {
  color: #ff5f5f;
  border-color: #ffb2b2;
  background: #fff5f5;
}
.st-withdraw {
  color: #e6a23c;
  border-color: #f5dab1;
  background: #fdf6ec;
}
.st-ended {
  color: #909399;
  border-color: #dcdfe6;
}
.head-meta-row {
  margin-top: 10px;
  font-size: 13px;
  color: #606266;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}
/* 触达漏斗 */
.funnel-section {
  margin-top: 22px;
}
.section-label {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
}
.funnel-list {
  display: flex;
  align-items: center;
}
.funnel-item {
  display: flex;
  align-items: center;
  position: relative;
}
.funnel-arrow {
  color: #c0c4cc;
  font-size: 16px;
  margin: 0 4px;
}
.funnel-arrow i {
  transform: scale(0.9);
}
.funnel-card {
  min-width: 110px;
  padding: 12px 8px;
  border-radius: 6px;
  text-align: center;
  border: 1px solid #ebeef5;
  background: #fff;
}
.funnel-num {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  line-height: 1.2;
}
.funnel-label {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}
.funnel-green .funnel-num {
  color: #10b981;
}
.funnel-red .funnel-num {
  color: #f56c6c;
}
.funnel-yellow .funnel-num {
  color: #e6a23c;
}
/* Tab + 搜索 */
.table-section {
  margin-top: 24px;
}
.tabs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  flex-wrap: wrap;
  gap: 10px;
}
.tabs {
  display: flex;
  align-items: center;
}
.tab-item {
  padding: 8px 14px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  border-radius: 4px;
  margin-right: 6px;
  user-select: none;
}
.tab-item.active {
  color: #409eff;
  background: #ecf5ff;
  font-weight: 600;
}
.tab-count {
  font-size: 12px;
  color: #909399;
  margin-left: 2px;
}
.tab-item.active .tab-count {
  color: #409eff;
}
.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
}
.search-box .el-input {
  width: 260px;
}
.search-box .el-input__icon.el-icon-search {
  cursor: pointer;
}
.search-box .el-input__icon.el-icon-search:hover {
  color: #409eff;
}
/* 表格 */
.cell-shop {
  color: #303133;
}
.shop-name {
  color: #303133;
  line-height: 1.4;
}
.shop-region {
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
}
/* 门店列合并后，内容在合并单元格内垂直居中 */
::v-deep .el-table__body tr > td:first-child {
  vertical-align: middle;
}
.cell-owner {
  line-height: 1.4;
}
.owner-name {
  color: #303133;
}
.owner-phone-line {
  display: flex;
  align-items: center;
}
.owner-phone {
  color: #909399;
  font-size: 12px;
}
.eye-toggle {
  margin-left: 6px;
  color: #909399;
  font-size: 14px;
  cursor: pointer;
  line-height: 1;
}
.eye-toggle:hover {
  color: #409eff;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
}
.status-icon {
  width: 17px;
  height: 17px;
  border-radius: 3px;
  margin-right: 5px;
  font-size: 11px;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-style: normal;
}
.status-icon-join {
  background: #10b981;
}
.status-icon-notjoin {
  background: #f56c6c;
}
.status-icon-unread {
  background: #e6a23c;
  border-radius: 50%;
}
.status-icon-read {
  background: #909399;
}
.status-icon-overdue {
  background: #ff5f5f;
  border-radius: 50%;
}
.badge-join {
  color: #10b981;
}
.badge-notjoin {
  color: #f56c6c;
}
.badge-unread {
  color: #e6a23c;
}
.badge-read {
  color: #909399;
}
.badge-overdue {
  color: #ff5f5f;
}
/* 否定理由 */
.neg-reason {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
}
.neg-reason-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  margin-right: 5px;
  color: #fff;
  border-radius: 50%;
  font-size: 11px;
  font-style: normal;
  flex: none;
}
/* 底部 */
.table-footer {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.footer-total {
  font-size: 13px;
  color: #909399;
}
</style>
